interface HttpOptions {
    headers?: Record<string, string>;
    signal?: AbortSignal;
    withCredentials?: boolean;
}

interface HttpResponse<T = any> {
    data: T;
    status: number;
    headers: Headers;
}

class HttpError extends Error {
    response: { status: number; data: any };

    constructor(status: number, data: any) {
        super(`Request failed with status ${status}`);
        this.name = 'HttpError';
        this.response = { status, data };
    }
}

async function request<T = any>(
    method: string,
    url: string,
    body?: unknown,
    options: HttpOptions = {},
): Promise<HttpResponse<T>> {
    const headers: Record<string, string> = { ...options.headers };

    const init: RequestInit = {
        method,
        headers,
        signal: options.signal,
        credentials: options.withCredentials ? 'include' : 'same-origin',
    };

    if (body !== undefined) {
        headers['Content-Type'] = 'application/json';
        init.body = JSON.stringify(body);
    }

    const res = await fetch(url, init);

    const ct = res.headers.get('content-type') ?? '';
    const data: T = ct.includes('application/json')
        ? await res.json()
        : (await res.text()) as unknown as T;

    if (!res.ok) {
        throw new HttpError(res.status, data);
    }

    return { data, status: res.status, headers: res.headers };
}

const http = {
    get<T = any>(url: string, options?: HttpOptions): Promise<HttpResponse<T>> {
        return request<T>('GET', url, undefined, options);
    },
    post<T = any>(url: string, body?: unknown, options?: HttpOptions): Promise<HttpResponse<T>> {
        return request<T>('POST', url, body, options);
    },
    delete<T = any>(url: string, options?: HttpOptions): Promise<HttpResponse<T>> {
        return request<T>('DELETE', url, undefined, options);
    },
    isAbortError(error: unknown): boolean {
        return error instanceof DOMException && error.name === 'AbortError';
    },
};

export default http;
export { HttpError };
export type { HttpResponse, HttpOptions };
